class StockAvailabilityWidget {
  constructor() {
    // Find main widget
    this.widget = document.querySelector('.stock-availability-widget');
    if (!this.widget) {
      console.error('Stock Availability Widget: Main widget element not found');
      return;
    }
    
    // Find form elements
    this.form = this.widget.querySelector('.stock-availability-form');
    this.input = this.widget.querySelector('.stock-availability-input');
    this.button = this.widget.querySelector('.stock-availability-button');
    this.results = this.widget.querySelector('.stock-availability-results');
    this.locationElem = this.widget.querySelector('.stock-availability-location');
    
    console.log('Widget elements found:', {
      form: !!this.form,
      input: !!this.input,
      button: !!this.button,
      results: !!this.results,
      location: !!this.locationElem
    });
    
    // Initialize API endpoint
    this.apiEndpoint = 'https://shopify-ccs.omf-aust.workers.dev/api/check-availability';
    
    // Initialize and show widget
    this.initializeWidget();
  }
  
  /**
   * Initialize the widget by setting up DOM elements and event handlers
   */
  initializeWidget() {
    // Hide results initially
    if (this.results) {
      this.results.style.display = 'none';
    }
    
    // Set up event handlers
    this.setupEventHandlers();
    
    // Check URL for postcode parameter
    setTimeout(() => {
      this.checkUrlForPostcode();
    }, 50); // Short delay to ensure DOM is ready
  }
  
  /**
   * Find and cache all required DOM elements
   */
  setupDomElements() {
    // Force a requery of section elements - they might not be ready when constructor runs
    this.setupSections();
  }
  
  /**
   * Get all results sections in the widget
   * @returns {NodeListOf<Element>} The sections found
   */
  setupSections() {
    if (!this.widget || !this.results) {
      console.error('Stock Availability Widget: Cannot find sections without widget or results container');
      return [];
    }
    
    // Always use direct query rather than first/last child selectors
    const allSections = this.results.querySelectorAll('.results-section');
    
    if (allSections.length >= 2) {
      this.inStoreSection = allSections[0];
      this.deliverySection = allSections[1];
      console.log('Found and assigned section elements:', {
        inStoreCount: this.inStoreSection.querySelectorAll('.stock-option').length,
        deliveryCount: this.deliverySection.querySelectorAll('.stock-option').length
      });
      
      // Check for and handle duplicate options
      this.checkForDuplicateOptions();
      
      return allSections;
    } else {
      console.error(`Stock Availability Widget: Expected 2 sections but found ${allSections.length}`);
      return [];
    }
  }
  
  /**
   * Check for and mark duplicate stock options
   */
  checkForDuplicateOptions() {
    if (!this.inStoreSection) {
      console.warn('Stock Availability Widget: In-store section not found, skipping duplicate check');
      return;
    }
    
    const pickupOptions = this.inStoreSection.querySelectorAll('.stock-option');
    console.log(`Found ${pickupOptions.length} pickup options`);
    
    if (pickupOptions.length > 2) {
      // Mark duplicates for hiding
      for (let i = 2; i < pickupOptions.length; i++) {
        pickupOptions[i].classList.add('duplicate-option');
        console.log(`Marked option ${i} as duplicate`);
      }
    }
  }
  
  /**
   * Set up event handlers for the form
   */
  setupEventHandlers() {
    if (!this.form || !this.input || !this.button) {
      console.error('Stock Availability Widget: Required form elements not found');
      return;
    }
    
    // Event listeners
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.handleSubmit(e);
      }
    });
    
    // Clear validation errors as user types
    this.input.addEventListener('input', () => {
      this.clearValidationError();
    });
    
    console.log('Event handlers set up successfully');
  }
  
  /**
   * Check URL for postcode parameter and run search if found
   */
  checkUrlForPostcode() {
    const params = new URLSearchParams(window.location.search);
    const postcodeParam = params.get('postcode');
    
    if (postcodeParam && this.input) {
      console.log('Found postcode in URL:', postcodeParam);
      this.input.value = postcodeParam;
      this.checkAvailability(postcodeParam);
    }
  }
  
  /**
   * Handle form submission
   */
  handleSubmit(e) {
    e.preventDefault();
    
    if (!this.input) {
      console.error('Stock Availability Widget: Input element not found');
      return;
    }
    
    const postcode = this.input.value.trim();
    if (!postcode) {
      console.warn('Stock Availability Widget: No postcode entered');
      return;
    }
    
    // Validate postcode format
    if (!this.validatePostcode(postcode)) {
      this.showValidationError('Enter valid postcode');
      return;
    }
    
    this.checkAvailability(postcode);
  }

  /**
   * Validate Australian postcode format
   * @param {string} postcode - The postcode to validate
   * @returns {boolean} Whether the postcode is valid
   */
  validatePostcode(postcode) {
    const postcodeRegex = /^\d{4}$/;
    const postcodeNum = parseInt(postcode);
    
    if (!postcodeRegex.test(postcode) || postcodeNum < 200 || postcodeNum > 9999) {
      return false;
    }
    
    return true;
  }

  /**
   * Show validation error message
   * @param {string} message - The error message to display
   */
  showValidationError(message) {
    // Hide any existing results completely
    if (this.results) {
      this.results.style.display = 'none';
      this.results.classList.remove('visible', 'error-state');
    }
    
    // Create or update a simple error message near the input
    this.showInputError(message);
    
    console.log('Validation error:', message);
  }

  /**
   * Show error message near the input field
   * @param {string} message - The error message to display
   */
  showInputError(message) {
    // Remove any existing error message
    const existingError = this.widget.querySelector('.validation-error-message');
    if (existingError) {
      existingError.remove();
    }
    
    // Create new error message element
    const errorElement = document.createElement('div');
    errorElement.className = 'validation-error-message';
    errorElement.textContent = message;
    errorElement.style.cssText = `
      color: #d32f2f;
      font-size: 14px;
      margin-top: 8px;
      padding: 8px 12px;
      background-color: #ffebee;
      border: 1px solid #ffcdd2;
      border-radius: 4px;
    `;
    
    // Insert after the form
    if (this.form) {
      this.form.after(errorElement);
    }
  }

  /**
   * Clear validation error message
   */
  clearValidationError() {
    const existingError = this.widget.querySelector('.validation-error-message');
    if (existingError) {
      existingError.remove();
    }
  }
  
  /**
   * Get product ID from widget data attribute
   * @returns {string} The product ID
   */
  getProductId() {
    // Get product ID from data attribute on the widget
    if (this.widget && this.widget.dataset.productId) {
      return this.widget.dataset.productId;
    }
    
    // Fallback: Try to get from meta tag
    const metaTag = document.querySelector('meta[property="og:product_id"]');
    if (metaTag && metaTag.content) {
      return metaTag.content;
    }
    
    console.warn('Stock Availability Widget: Product ID not found');
    return '';
  }

  getVariantId() {
    const variantId = document.querySelector('#product-form-template--19016148385989__main > input.product-variant-id')?.value;
    if (variantId) {
      return variantId;
    }
    console.warn('Stock Availability Widget: Variant ID not found');
    return '';
  }
  
  /**
   * Check availability for a given postcode
   */
  async checkAvailability(postcode) {
    // Verify required elements exist
    if (!this.widget || !this.results || !this.button) {
      console.error('Stock Availability Widget: Required elements not found');
      return;
    }
    
    // Clear any validation errors
    this.clearValidationError();
    
    // Force re-query of section elements
    this.setupSections();
    
    // Reset any previous error state
    this.results.classList.remove('error-state');
    
    // Show loading state
    this.showLoadingState();
    
    try {
      // Get product ID from the widget data attribute
      const productId = this.getProductId();

      const variantId = this.getVariantId();
      
      
      if (!productId) {
        throw new Error('Product ID not found');
      }

      if (!variantId) {
        console.warn('Stock Availability Widget: Variant ID not found, using product ID');
      }
      
      console.log('Checking availability for product:', productId, 'postcode:', postcode);
      
      // Make API call
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productId: productId,
          variantId: variantId,
          postcode: postcode
        })
      });
      
      const data = await response.json();
      console.log('API response received:', data);
      
      // Check if API returned error status or invalid response
      if (!response.ok || data.error) {
        throw new Error(data.error || 'Failed to check availability');
      }
      
      // Update UI with response data
      this.updateAvailabilityInfo(data, postcode);
      
      // Update URL with postcode parameter without refreshing the page
      const url = new URL(window.location);
      url.searchParams.set('postcode', postcode);
      window.history.replaceState({}, '', url);
      
      // Show results after successfully updating
      this.showResults();
    } catch (error) {
      console.error('Stock Availability Widget: Error checking availability:', error);
      // Handle error in UI
      this.showErrorState(postcode);
    } finally {
      // Reset button and remove loading state
      this.hideLoadingState();
    }
  }
  
  /**
   * Show loading skeleton state
   */
  showLoadingState() {
    // Ensure results are visible but empty
    if (this.results) {
      this.results.style.display = 'block';
      this.results.classList.add('visible', 'loading');
      
      // Add loading state to sections
      const sections = this.results.querySelectorAll('.results-section');
      sections.forEach(section => {
        section.classList.add('loading');
      });
      
      // Add loading state to location name
      const locationName = this.results.querySelector('.location-name');
      if (locationName) {
        locationName.classList.add('loading', 'skeleton-loading');
      }
      
      console.log('Showing skeleton loading state');
    }
    
    // Disable button during loading
    if (this.button) {
      this.button.disabled = true;
    }
  }
  
  /**
   * Hide loading skeleton state
   */
  hideLoadingState() {
    // Remove loading state from results
    if (this.results) {
      this.results.classList.remove('loading');
      
      // Remove loading state from sections
      const sections = this.results.querySelectorAll('.results-section');
      sections.forEach(section => {
        section.classList.remove('loading');
      });
      
      // Remove loading state from location name
      const locationName = this.results.querySelector('.location-name');
      if (locationName) {
        locationName.classList.remove('loading', 'skeleton-loading');
      }
      
      console.log('Hiding skeleton loading state');
    }
    
    // Re-enable button after loading
    if (this.button) {
      this.button.disabled = false;
    }
  }
  
  /**
   * Show error state in the UI
   */
  showErrorState(postcode) {
    // Verify required elements exist
    if (!this.widget || !this.results) {
      console.error('Stock Availability Widget: Required elements not found for error state');
      return;
    }
    
    // Add error class to results container
    this.results.classList.add('error-state');
    
    // Set error message in location area
    if (this.locationElem) {
      const locationNameSpan = this.locationElem.querySelector('.location-name');
      const directionsLink = this.locationElem.querySelector('.directions-link');
      
      // Update the error message
      if (locationNameSpan) {
        locationNameSpan.textContent = 'Error checking availability';
      } else {
        this.locationElem.textContent = 'Error checking availability';
      }
      
      // Hide directions link if it exists
      if (directionsLink) {
        directionsLink.style.display = 'none';
      }
    }
    
    // Hide shipping banner
    const shippingBanner = this.results.querySelector('.shipping-info-banner');
    if (shippingBanner) {
      shippingBanner.style.display = 'none';
    }
    
    // Grey out all availability indicators
    const allDots = this.widget.querySelectorAll('.stock-option-dot');
    allDots.forEach(dot => {
      dot.classList.remove('available');
    });
    
    // Show the results with error state
    this.showResults();
  }
  
  /**
   * Force green dots directly for maximum reliability
   * @param {boolean} inStore - Whether in-store is available
   * @param {boolean} pickup - Whether pickup is available
   * @param {boolean} delivery - Whether delivery is available
   */
  forceGreenDots(inStore, pickup, delivery) {
    console.log('Forcing green dots with:', { inStore, pickup, delivery });
    
    // Get all dots directly
    const dots = this.widget.querySelectorAll('.stock-option-dot');
    console.log(`Found ${dots.length} dots to update`);
    
    // Apply classes to ensure they show correctly (don't override with inline styles)
    if (dots.length >= 1) {
      if (inStore) {
        dots[0].classList.add('available');
      } else {
        dots[0].classList.remove('available');
        dots[0].style.backgroundColor = '#ccc';
      }
      console.log('Updated in-store dot class');
    }
    
    if (dots.length >= 2) {
      if (pickup) {
        dots[1].classList.add('available');
      } else {
        dots[1].classList.remove('available');
        dots[1].style.backgroundColor = '#ccc';
      }
      console.log('Updated pickup dot class');
    }
    
    if (dots.length >= 3) {
      if (delivery) {
        dots[2].classList.add('available');
      } else {
        dots[2].classList.remove('available');
        dots[2].style.backgroundColor = '#ccc';
      }
      console.log('Updated delivery dot class');
    }
  }
  
  /**
   * Update the UI with availability information
   */
  updateAvailabilityInfo(data, postcode) {
    // Bail early if we don't have data or required elements
    if (!data || !this.widget || !this.results) {
      console.error('Stock Availability Widget: Missing data or elements');
      return;
    }
    
    console.log('Updating availability info with data:', JSON.stringify(data));
    
    // Check if product is dropshipped
    const isDropship = data.fulfillmentInfo?.fulfillmentType === 'Dropship';
    console.log('Product is dropshipped:', isDropship);
    
    // Update location
    if (this.locationElem) {
      // Find location name span and directions link
      const locationNameSpan = this.locationElem.querySelector('.location-name');
      const directionsLink = this.locationElem.querySelector('.directions-link');
      
      // If the API returns a nearestStore name, use that, otherwise use the postcode
      const locationName = data.nearestStore?.name || postcode;
      // let locationName = data.postcodeServiceability?.fulfillmentInfo.fulfillmentType === "DC" ?
      //   data.postcodeServiceability?.fulfillmentInfo?.dcLocation || postcode : data.nearestStore?.name || postcode;
      
      // Update the location name in the span
      if (locationNameSpan) {
        locationNameSpan.textContent = locationName;
      } else {
        // Fallback if span not found - update the entire element
        this.locationElem.textContent = locationName;
      }
      
      // If we have a directions link and store coordinates, update the link
      if (directionsLink && data.nearestStore) {
        const { lat, lng, name, address, city, postcode } = data.nearestStore;
        if (lat && lng) {
          // Create Google Maps URL with coordinates
          const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&destination_place_id=${encodeURIComponent(name + ' ' + address + ' ' + city + ' ' + postcode)}`;
          directionsLink.href = mapsUrl;
          directionsLink.style.display = 'flex'; // Show the button
          console.log('Updated directions link with coordinates');
        } else {
          // Hide the link if no coordinates
          directionsLink.style.display = 'none';
          console.log('No coordinates available, hiding directions link');
        }
      }
      
      console.log('Updated location text to:', locationName);
    }
    
    // Update shipping information
    this.updateShippingInfo(data.shipping);
    
    // Re-query section elements to ensure they're available
    const sections = this.setupSections();
    
    // If we couldn't find sections, bail out
    if (!this.inStoreSection || !this.deliverySection) {
      console.error('Stock Availability Widget: Could not find required sections after multiple attempts');
      return;
    }
    
    // Original availability logic suspended until further notice
    // const isServiceable = data.postcodeServiceability?.isServiceable === true;
    // const isInStock = data.postcodeServiceability?.inStock === true;
    // const inStoreAvailable = isServiceable && isInStock && !isDropship;
    // const pickupAvailable = isServiceable && isInStock && !isDropship;
    // const deliveryAvailable = data.shippingStatus?.includes('Free Delivery Available') === true;

    // Vendor override: use data-product-omf
    const isOmfVendor = this.widget.dataset.productOmf?.trim() === 'true';
    const inStoreAvailable = isOmfVendor;
    const pickupAvailable = isOmfVendor;
    const deliveryAvailable = true; // always available
    const dropshipMessage = this.widget.dataset.dropshipMessage;

    // Ensure free delivery for non-OMF vendors
    if (!isOmfVendor && data.shipping) {
      data.shipping.cost = 0;
      data.shipping.formatted = '$0.00';
    }
    
    console.log('Availability status:', {
      inStoreAvailable,
      pickupAvailable,
      deliveryAvailable,
      isDropship
    });
    
    // Add dropship notice if needed
    this.showDropshipNotice(isDropship, data.fulfillmentInfo?.message);
    
    // Get all stock options by data attribute
    const tryInStoreOption = this.widget.querySelector('[data-option-type="try-instore"]');
    const pickupOption = this.widget.querySelector('[data-option-type="pickup"]');
    const deliveryOption = this.widget.querySelector('[data-option-type="delivery"]');
    
    console.log('Options found by data attribute:', {
      tryInStoreOption: !!tryInStoreOption,
      pickupOption: !!pickupOption,
      deliveryOption: !!deliveryOption,
      dropshipMessage: dropshipMessage
    });
    
    // Update option availability
    this.updateStockOptionAvailability(
      tryInStoreOption,
      inStoreAvailable,
      isDropship,
      this.widget?.dataset?.tryInstoreAvailable || 'Available to try in-store',
      this.widget?.dataset?.tryInstoreUnavailable || 'Not available to try in-store',
      this.widget?.dataset?.dropshipMessage || 'This item ships directly from the manufacturer'
    );

    this.updateStockOptionAvailability(
      pickupOption,
      pickupAvailable,
      isDropship,
      this.widget?.dataset?.pickupAvailable || 'Free store pickup available. We\'ll confirm your pickup date',
      this.widget?.dataset?.pickupUnavailable || 'Not available for Click & Collect',
      this.widget?.dataset?.dropshipMessage || 'This item ships directly from the manufacturer'
    );

    this.updateStockOptionAvailability(
      deliveryOption,
      deliveryAvailable,
      false,
      dropshipMessage != '' ? dropshipMessage : 'Available to order. Our team will contact you to arrange your delivery date.',
      this.widget?.dataset?.deliveryUnavailable || 'Not available for delivery in your area',
      ''
    );
    
    // Force apply green dots using most reliable method
    this.forceGreenDots(inStoreAvailable, pickupAvailable, deliveryAvailable);
    
    // Print current dot status to console
    this.printDotStatus();
  }
  
  /**
   * Update stock option availability
   * @param {Element} option - The stock option element
   * @param {boolean} isAvailable - Whether the option is available
   * @param {boolean} isDropship - Whether the product is dropshipped
   * @param {string} availableText - Text to show when available
   * @param {string} unavailableText - Text to show when unavailable
   * @param {string} tooltip - Tooltip text for unavailable state
   */
  updateStockOptionAvailability(option, isAvailable, isDropship, availableText, unavailableText, tooltip) {
    if (!option) {
      return;
    }
    
    const dot = option.querySelector('.stock-option-dot');
    const title = option.querySelector('.stock-option-title');
    
    if (!dot || !title) {
      return;
    }
    
    // Update dot
    if (isAvailable) {
      dot.classList.add('available');
      dot.style.removeProperty('background-color');
      console.log(`Added available class to ${option.dataset.optionType} dot`);
    } else {
      dot.classList.remove('available');
      dot.style.backgroundColor = '#ccc';
      console.log(`Removed available class from ${option.dataset.optionType} dot`);
    }
    
    // Update title text
    if (isDropship && ['try-instore', 'pickup'].includes(option.dataset.optionType)) {
      title.innerHTML = `<span class="unavailable-message">${unavailableText}</span>`;
      title.title = tooltip;
      console.log(`Updated ${option.dataset.optionType} title for dropship product`);
    } else if (!isAvailable) {
      title.innerHTML = `<span class="unavailable-message">${unavailableText}</span>`;
      console.log(`Updated ${option.dataset.optionType} title for unavailable product`);
    } else {
      title.textContent = availableText;
      console.log(`Updated ${option.dataset.optionType} title for available product`);
    }
  }
  
  /**
   * Update shipping information in the UI
   * @param {Object} shipping - Shipping data from API response
   */
  updateShippingInfo(shipping) {
    if (!this.results) return;
    
    const shippingBanner = this.results.querySelector('.shipping-info-banner');
    if (!shippingBanner) {
      console.warn('Shipping banner element not found');
      return;
    }
    
    const costValue = shippingBanner.querySelector('.shipping-cost-value');
    const description = shippingBanner.querySelector('.shipping-description');
    
    // Hide banner by default
    shippingBanner.style.display = 'none';
    
    // If we have shipping data, show and update the banner
    if (shipping) {
      console.log('Updating shipping info:', shipping);
      
      // Show banner
      shippingBanner.style.display = 'flex';
      
      // Update cost value
      if (costValue) {
        const isFree =  this.widget.dataset.productOmf?.trim() === 'false';
        console.log('Shipping cost is free:', isFree);
        costValue.textContent = isFree === true ? 'FREE' : `$${shipping.cost}`;
        costValue.classList.toggle('free', isFree);
      }
      
      // Update description
      if (description) {
        description.textContent = shipping.description || '';
      }
    }
  }
  
  /**
   * Show a dropship notice in the UI
   * @param {boolean} isDropship - Whether product is dropshipped
   * @param {string} message - Optional dropship message from API
   */
  showDropshipNotice(isDropship, message) {
    // Remove any existing notice first
    const existingNotice = this.widget.querySelector('.dropship-notice');
    if (existingNotice) {
      existingNotice.remove();
    }
    
    // If product is dropshipped, add a notice
    if (isDropship && this.results) {
      const notice = document.createElement('div');
      notice.className = 'dropship-notice';
      notice.textContent = message || 'This item ships directly from the manufacturer';
      
      // Insert after the location element
      if (this.locationElem) {
        this.locationElem.after(notice);
        console.log('Added dropship notice');
      }
    }
  }
  
  /**
   * Debug helper: Print the current status of all dots
   */
  printDotStatus() {
    const dots = this.widget.querySelectorAll('.stock-option-dot');
    
    console.log('===== DOT STATUS =====');
    
    dots.forEach((dot, index) => {
      const computed = window.getComputedStyle(dot);
      const hasClass = dot.classList.contains('available');
      const bgColor = computed.backgroundColor;
      const inlineStyle = dot.style.backgroundColor;
      
      console.log(`Dot ${index}:`);
      console.log(`- Has 'available' class: ${hasClass}`);
      console.log(`- Computed bg color: ${bgColor}`);
      console.log(`- Inline style bg: ${inlineStyle}`);
      console.log(`- HTML: ${dot.outerHTML}`);
      console.log('-------------------');
    });
    
    console.log('======================');
  }
  
  /**
   * Show the results container with animation
   */
  showResults() {
    if (!this.results) return;
    
    console.log('Showing results');
    
    // Make sure the results element is visible
    this.results.style.display = 'block';
    
    // Force refresh of all dots to make sure the styling is applied
    const dots = this.widget.querySelectorAll('.stock-option-dot.available');
    dots.forEach(dot => {
      // Remove any inline styles that might conflict with the animation
      dot.style.removeProperty('background-color');
      console.log('Prepared dot for animation during results show');
    });
    
    // Add the visible class for animation
    // Use a timeout to ensure display:block has taken effect
    setTimeout(() => {
      this.results.classList.add('visible');
      
      // Double-check dots after animation
      setTimeout(() => {
        this.forceRefreshDots();
      }, 100);
    }, 10);
  }
  
  /**
   * Force refresh the appearance of all dots
   */
  forceRefreshDots() {
    console.log('Force refreshing dots');
    const availableDots = this.widget.querySelectorAll('.stock-option-dot.available');
    const unavailableDots = this.widget.querySelectorAll('.stock-option-dot:not(.available)');
    
    // Only set color on unavailable dots - let CSS handle the animation for available dots
    unavailableDots.forEach(dot => {
      dot.style.backgroundColor = '#ccc';
    });
  }
  
  /**
   * Hide the results container with animation
   */
  hideResults() {
    if (!this.results) return;
    
    console.log('Hiding results');
    
    // Remove the visible class first
    this.results.classList.remove('visible');
    
    // Set display none after animation completes
    setTimeout(() => {
      if (!this.results.classList.contains('visible')) {
        this.results.style.display = 'none';
      }
    }, 300); // Match transition duration
  }
}

// Initialize widget only when DOM is fully loaded
document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM loaded, initializing stock availability widget');
  new StockAvailabilityWidget();
});

// Support for Shopify section rendering
document.addEventListener('shopify:section:load', (event) => {
  console.log('Section loaded, checking for stock availability widget');
  if (event.target.querySelector('.stock-availability-widget')) {
    console.log('Widget found in loaded section, initializing');
    new StockAvailabilityWidget();
  }
}); 